import React, { FormEvent, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  Clock,
  Filter,
  Loader2,
  Mail,
  MessageCircle,
  Plus,
  RefreshCcw,
  Send,
  User,
} from 'lucide-react';
import { inquiriesApi, InquiryDetail, InquiryMessage, InquiryStatus, InquirySummary } from '../api';
import { useAuth } from '../hooks/useAuth';

type StatusFilter = 'ALL' | InquiryStatus;

const statusFilters: Array<{ value: StatusFilter; label: string }> = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'Open', label: 'Open' },
  { value: 'InProgress', label: 'In progress' },
  { value: 'Resolved', label: 'Resolved' },
  { value: 'Closed', label: 'Closed' },
];

const statusBadgeStyles: Record<InquiryStatus, string> = {
  Open: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
  InProgress: 'border border-amber-200 bg-amber-50 text-amber-700',
  Resolved: 'border border-blue-200 bg-blue-50 text-blue-700',
  Closed: 'border border-slate-300 bg-slate-200 text-slate-700',
};

const updateStatuses: InquiryStatus[] = ['Open', 'InProgress', 'Resolved', 'Closed'];

const formatStatus = (status: InquiryStatus) => (status === 'InProgress' ? 'In progress' : status);

const formatDateTime = (value?: string | null) => {
  if (!value) return '�';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '�';
  return date.toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const roleLabel = (role?: string | null) => {
  if (!role) return 'System';
  return role
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const InquiriesSupport: React.FC = () => {
  const { user } = useAuth();
  const isSystemAdmin = user?.role === 'SYSTEM_ADMIN';
  const isTrainee = user?.role === 'TRAINEE';

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, limit: 10, totalPages: 1 });
  const [listLoading, setListLoading] = useState(false);
  const [inquiries, setInquiries] = useState<InquirySummary[]>([]);

  const [activeInquiryId, setActiveInquiryId] = useState<number | null>(null);
  const activeIdRef = useRef<number | null>(null);
  const pendingSelectRef = useRef<number | null>(null);

  const [detail, setDetail] = useState<InquiryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [createExpanded, setCreateExpanded] = useState(false);
  const [createMessage, setCreateMessage] = useState('');
  const [creating, setCreating] = useState(false);

  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const [statusValue, setStatusValue] = useState<InquiryStatus>('Open');
  const [statusNote, setStatusNote] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const pageSize = 10;

  useEffect(() => {
    activeIdRef.current = activeInquiryId;
  }, [activeInquiryId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setListLoading(true);
      try {
        const response = await inquiriesApi.list({
          page,
          limit: pageSize,
          status: statusFilter === 'ALL' ? undefined : statusFilter,
        });

        if (cancelled) return;

        if (response.success && response.data) {
          const rows = response.data;
          setInquiries(rows);
          setMeta({
            total: response.meta?.total ?? rows.length,
            limit: response.meta?.limit ?? pageSize,
            totalPages: response.meta?.totalPages ?? 1,
          });

          if (rows.length === 0) {
            setActiveInquiryId(null);
            setDetail(null);
          } else {
            const currentActive = activeIdRef.current;
            const pendingId = pendingSelectRef.current;
            let nextId: number | null = null;

            if (pendingId && rows.some((row) => row.inquiry_id === pendingId)) {
              nextId = pendingId;
            } else if (currentActive && rows.some((row) => row.inquiry_id === currentActive)) {
              nextId = currentActive;
            } else {
              nextId = rows.length > 0 ? rows[0].inquiry_id : null;
            }

            if (nextId !== currentActive) {
              setActiveInquiryId(nextId);
            }

            if (pendingId && nextId === pendingId) {
              pendingSelectRef.current = null;
            }
          }
        } else {
          setInquiries([]);
          setMeta({ total: 0, limit: pageSize, totalPages: 1 });
          setActiveInquiryId(null);
          setDetail(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          alert('Failed to load inquiries');
        }
      } finally {
        if (!cancelled) {
          setListLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [page, statusFilter]);

  useEffect(() => {
    if (!activeInquiryId) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);

    (async () => {
      try {
        const response = await inquiriesApi.get(activeInquiryId);
        if (cancelled) return;

        if (response.success && response.data) {
          setDetail(response.data);
        } else {
          setDetail(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          alert('Failed to load inquiry details');
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeInquiryId]);

  useEffect(() => {
    if (detail) {
      setStatusValue(detail.status);
      setStatusNote('');
    }
  }, [detail?.inquiry_id, detail?.status]);

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = createMessage.trim();
    if (!trimmed) return;

    setCreating(true);
    try {
      const response = await inquiriesApi.create({ message: trimmed });
      if (response.success && response.data) {
        alert('Inquiry submitted successfully.');
        pendingSelectRef.current = response.data.inquiry_id;
        setCreateMessage('');
        setCreateExpanded(false);
        setStatusFilter('Open');
        setPage(1);
      } else {
        alert(response.message || 'Failed to create inquiry');
      }
    } catch (error: any) {
      console.error(error);
      alert(error?.response?.data?.message || 'Failed to create inquiry');
    } finally {
      setCreating(false);
    }
  };

  const handleSendMessage = async (event: FormEvent) => {
    event.preventDefault();
    if (!detail) return;
    const trimmed = replyMessage.trim();
    if (!trimmed) return;

    setSendingReply(true);
    try {
      const response = await inquiriesApi.addMessage(detail.inquiry_id, { message: trimmed });
      if (response.success && response.data) {
        const message = response.data as InquiryMessage;
        setDetail((prev) => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            last_message_at: message.created_at,
            latest_message: message,
            messages: [...prev.messages, message],
          };
        });
        setInquiries((prev) =>
          prev.map((item) =>
            item.inquiry_id === detail.inquiry_id
              ? {
                  ...item,
                  last_message_at: message.created_at,
                  latest_message: message,
                }
              : item
          )
        );
        setReplyMessage('');
      } else {
        alert(response.message || 'Failed to send message');
      }
    } catch (error: any) {
      console.error(error);
      alert(error?.response?.data?.message || 'Failed to send message');
    } finally {
      setSendingReply(false);
    }
  };

  const handleStatusUpdate = async (event: FormEvent) => {
    event.preventDefault();
    if (!detail) return;
    const trimmedNote = statusNote.trim();
    if (statusValue === detail.status && !trimmedNote) return;

    setUpdatingStatus(true);
    try {
      const response = await inquiriesApi.updateStatus(detail.inquiry_id, {
        status: statusValue,
        note: trimmedNote || undefined,
      });
      if (response.success && response.data) {
        const message = response.data as InquiryMessage;
        setDetail((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            status: statusValue,
            resolved_at: statusValue === 'Resolved' ? message.created_at : null,
            closed_at: statusValue === 'Closed' ? message.created_at : null,
            last_message_at: message.created_at,
            latest_message: message,
            messages: [...prev.messages, message],
          };
        });
        setInquiries((prev) =>
          prev.map((item) =>
            item.inquiry_id === detail.inquiry_id
              ? {
                  ...item,
                  status: statusValue,
                  resolved_at: statusValue === 'Resolved' ? message.created_at : null,
                  closed_at: statusValue === 'Closed' ? message.created_at : null,
                  last_message_at: message.created_at,
                  latest_message: message,
                }
              : item
          )
        );
        setStatusNote('');
        alert('Status updated.');
      } else {
        alert(response.message || 'Failed to update status');
      }
    } catch (error: any) {
      console.error(error);
      alert(error?.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const renderMessage = (message: InquiryMessage) => {
    if (message.message_type === 'STATUS_CHANGE') {
      return (
        <div key={message.message_id} className="flex justify-center">
          <div className="flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-700">
            <RefreshCcw className="h-3.5 w-3.5" />
            <span>{message.body}</span>
            <span className="text-[10px] text-amber-500">{formatDateTime(message.created_at)}</span>
          </div>
        </div>
      );
    }

    const isCurrentUser = user?.user_id && message.sender_id === user.user_id;

    return (
      <div
        key={message.message_id}
        className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse text-right' : ''}`}
      >
        <div
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
            isCurrentUser ? 'bg-primary-100 text-primary-600' : 'bg-gray-200 text-gray-600'
          }`}
        >
          <User className="h-4 w-4" />
        </div>
        <div className="max-w-xl">
          <div className="text-xs text-gray-500">
            {message.sender?.full_name || roleLabel(message.sender_role)}
          </div>
          <div
            className={`mt-1 inline-block rounded-2xl px-4 py-2 text-sm shadow ${
              isCurrentUser
                ? 'bg-primary-500 text-white'
                : 'border border-gray-200 bg-white text-gray-800'
            }`}
          >
            <p className="whitespace-pre-wrap break-words">{message.body}</p>
          </div>
          <div className="mt-1 text-xs text-gray-400">{formatDateTime(message.created_at)}</div>
        </div>
      </div>
    );
  };

  const currentStatusLabel = detail ? formatStatus(detail.status) : '�';
  const disableStatusUpdate = !detail || updatingStatus || (statusValue === detail.status && !statusNote.trim());
  const canReply = detail && detail.status !== 'Closed';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Inquiries &amp; Support</h1>
          <p className="text-sm text-gray-500">
            Track trainee questions, collaborate across teams, and keep everyone informed.
          </p>
        </div>
      </div>

      {isTrainee && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex items-start justify-between px-6 py-4">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-gray-800">
                <MessageCircle className="h-5 w-5 text-primary-500" />
                Need help?
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Raise an inquiry and the mobilisation partner, center admin, and system admin will be notified.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setCreateExpanded((prev) => !prev);
                if (createExpanded) {
                  setCreateMessage('');
                }
              }}
              className="inline-flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-100"
            >
              {createExpanded ? (
                <>
                  <AlertTriangle className="h-4 w-4" />
                  Cancel
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  New inquiry
                </>
              )}
            </button>
          </div>

          {createExpanded ? (
            <form onSubmit={handleCreate} className="px-6 pb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="new-inquiry-message">
                Describe your issue
              </label>
              <textarea
                id="new-inquiry-message"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                rows={5}
                maxLength={2000}
                value={createMessage}
                onChange={(event) => setCreateMessage(event.target.value)}
                placeholder="Share what you need help with. Include any relevant details so the support team can respond quickly."
                required
              />
              <div className="mt-3 flex justify-end gap-3">
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
                  onClick={() => {
                    setCreateExpanded(false);
                    setCreateMessage('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || createMessage.trim().length < 5}
                  className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Submit inquiry
                </button>
              </div>
            </form>
          ) : (
            <div className="px-6 pb-6 text-sm text-gray-500">
              You can track responses and continue the conversation from this page.
            </div>
          )}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-1">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Filter className="h-4 w-4 text-gray-400" />
                Filters
              </span>
            </div>
            <div className="mt-3">
              <label htmlFor="status-filter" className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Status
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value as StatusFilter);
                  setPage(1);
                }}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {statusFilters.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-3">
            {listLoading ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-white py-12 text-sm text-gray-500">
                <Loader2 className="mb-2 h-5 w-5 animate-spin text-primary-500" />
                Loading inquiries...
              </div>
            ) : inquiries.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
                <MessageCircle className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-3 font-medium">No inquiries found</p>
                <p className="mt-1 text-sm text-gray-500">
                  {isTrainee
                    ? 'You have not raised any inquiries yet.'
                    : 'There are currently no inquiries assigned to you.'}
                </p>
                {isTrainee && !createExpanded && (
                  <button
                    type="button"
                    className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
                    onClick={() => setCreateExpanded(true)}
                  >
                    <Plus className="h-4 w-4" />
                    Start a new inquiry
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {inquiries.map((item) => {
                  const isActive = item.inquiry_id === activeInquiryId;
                  const lastMessage = item.latest_message;
                  const previewText = lastMessage
                    ? lastMessage.message_type === 'STATUS_CHANGE'
                      ? `Status update: ${lastMessage.body}`
                      : lastMessage.body
                    : 'Awaiting response';

                  return (
                    <button
                      key={item.inquiry_id}
                      type="button"
                      onClick={() => setActiveInquiryId(item.inquiry_id)}
                      className={`w-full rounded-lg border px-4 py-4 text-left shadow-sm transition ${
                        isActive
                          ? 'border-primary-400 bg-primary-50'
                          : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50/40'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {item.trainee?.full_name || 'Unknown trainee'}
                          </div>
                          <div className="text-xs text-gray-500">
                            #{item.inquiry_id} � {formatStatus(item.status)}
                          </div>
                        </div>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeStyles[item.status]}`}>
                          {formatStatus(item.status)}
                        </span>
                      </div>
                      <div
                        className="mt-3 max-h-12 overflow-hidden text-sm text-gray-600"
                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                      >
                        {previewText}
                      </div>
                      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5 text-gray-400" />
                          {formatDateTime(item.last_message_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3.5 w-3.5 text-gray-400" />
                          {item.batch?.batch_code || 'Not linked'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {!listLoading && meta.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 pt-3 text-xs text-gray-500">
              <button
                type="button"
                className="rounded-md border border-gray-200 px-3 py-1.5 font-medium hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
              >
                Previous
              </button>
              <span>
                Page {page} of {meta.totalPages}
              </span>
              <button
                type="button"
                className="rounded-md border border-gray-200 px-3 py-1.5 font-medium hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => setPage((prev) => Math.min(meta.totalPages, prev + 1))}
                disabled={page >= meta.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4 lg:col-span-2">
          {detailLoading && !detail ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-white py-16 text-sm text-gray-500">
              <Loader2 className="mb-3 h-6 w-6 animate-spin text-primary-500" />
              Loading inquiry...
            </div>
          ) : !detail ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center text-gray-500">
              <MessageCircle className="mx-auto h-10 w-10 text-gray-400" />
              <h3 className="mt-4 text-base font-semibold text-gray-700">Select an inquiry</h3>
              <p className="mt-2 text-sm text-gray-500">
                Choose an inquiry from the list to view its details and conversation timeline.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-6 py-4">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Inquiry #{detail.inquiry_id}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                    <Clock className="h-4 w-4 text-gray-400" />
                    Updated {formatDateTime(detail.last_message_at)}
                  </div>
                </div>
                <span className={`rounded-full px-3 py-1 text-sm font-medium ${statusBadgeStyles[detail.status]}`}>
                  {currentStatusLabel}
                </span>
              </div>

              {isSystemAdmin && (
                <form onSubmit={handleStatusUpdate} className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                  <div className="mb-3 text-sm font-semibold text-gray-700">Update status</div>
                  <div className="flex flex-col gap-3 md:flex-row">
                    <select
                      value={statusValue}
                      onChange={(event) => setStatusValue(event.target.value as InquiryStatus)}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 md:max-w-[180px]"
                    >
                      {updateStatuses.map((status) => (
                        <option key={status} value={status}>
                          {formatStatus(status)}
                        </option>
                      ))}
                    </select>
                    <textarea
                      value={statusNote}
                      onChange={(event) => setStatusNote(event.target.value)}
                      placeholder="Add an optional note for this update"
                      className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      rows={2}
                      maxLength={2000}
                    />
                    <button
                      type="submit"
                      disabled={disableStatusUpdate}
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {updatingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
                      Update
                    </button>
                  </div>
                </form>
              )}

              <div className="grid gap-4 px-6 py-4 md:grid-cols-2">
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Trainee</div>
                  <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <User className="h-4 w-4 text-primary-500" />
                    {detail.trainee?.full_name || 'Unknown trainee'}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">{detail.trainee?.email || '�'}</div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Batch</div>
                  <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <Building2 className="h-4 w-4 text-primary-500" />
                    {detail.batch?.batch_code || 'Not linked'}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">{detail.batch?.program_name || '�'}</div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Mobilisation partner</div>
                  <div className="mt-1 text-sm font-semibold text-gray-900">
                    {detail.mp_partner?.name || 'Not linked'}
                  </div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Center admin</div>
                  <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <Mail className="h-4 w-4 text-primary-500" />
                    {detail.center_admin?.full_name || 'Not assigned'}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">{detail.center_admin?.email || '�'}</div>
                </div>
              </div>

              <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                <div className="mb-3 text-sm font-semibold text-gray-700">Conversation</div>
                {detailLoading && (
                  <div className="mb-3 flex items-center gap-2 text-xs text-gray-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-primary-500" />
                    Refreshing...
                  </div>
                )}
                <div className="max-h-[420px] space-y-5 overflow-y-auto pr-1">
                  {detail.messages.map((message) => renderMessage(message))}
                </div>
              </div>

              <div className="border-t border-gray-200 px-6 py-4">
                {canReply ? (
                  <form onSubmit={handleSendMessage} className="space-y-3">
                    <textarea
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      rows={3}
                      placeholder="Write a reply"
                      value={replyMessage}
                      onChange={(event) => setReplyMessage(event.target.value)}
                      maxLength={2000}
                      required
                    />
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Replies are visible to all stakeholders on this inquiry.</span>
                      <button
                        type="submit"
                        disabled={sendingReply || !replyMessage.trim()}
                        className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {sendingReply ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Send reply
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                    <AlertTriangle className="h-4 w-4 text-gray-400" />
                    This inquiry is closed. Only system admins can reopen it by changing the status.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InquiriesSupport;


